export type ReliefReason = 'network' | 'cpu';

export type ReliefPhase = 'idle' | 'promptPause' | 'pausing' | 'paused' | 'promptResume' | 'resuming';

export interface ReliefSample {
    readonly degraded: ReliefReason | null;
    readonly healthy: boolean;
    readonly videoActive: boolean;
}

export const DEGRADED_MS = 6_000;
export const HEALTHY_MS = 20_000;
export const COOLDOWN_MS = 5 * 60_000;

export class VideoReliefAdvisor {
    private current: ReliefPhase = 'idle';
    private currentReason: ReliefReason = 'network';
    private degradedSince: number | undefined;
    private healthySince: number | undefined;
    private quietUntil = 0;

    get phase(): ReliefPhase {
        return this.current;
    }

    get reason(): ReliefReason {
        return this.currentReason;
    }

    sample(now: number, sample: ReliefSample): ReliefPhase {
        switch (this.current) {
            case 'idle':
                this.watchForDegradation(now, sample);
                break;
            case 'promptPause':
                if (!sample.videoActive || !sample.degraded) {
                    this.toIdle();
                } else {
                    this.currentReason = sample.degraded;
                }
                break;
            case 'paused':
                this.watchForRecovery(now, sample);
                break;
            case 'promptResume':
                if (!sample.healthy) {
                    this.current = 'paused';
                    this.healthySince = undefined;
                }
                break;
            default:
                break;
        }
        return this.current;
    }

    acceptPause(): boolean {
        if (this.current !== 'promptPause') {
            return false;
        }
        this.current = 'pausing';
        return true;
    }

    pauseSucceeded(): void {
        if (this.current === 'pausing') {
            this.current = 'paused';
            this.healthySince = undefined;
        }
    }

    pauseFailed(): void {
        if (this.current === 'pausing') {
            this.toIdle();
        }
    }

    acceptResume(): boolean {
        if (this.current !== 'promptResume' && this.current !== 'paused') {
            return false;
        }
        this.current = 'resuming';
        return true;
    }

    resumeSucceeded(): void {
        if (this.current === 'resuming') {
            this.toIdle();
        }
    }

    resumeFailed(): void {
        if (this.current === 'resuming') {
            this.current = 'paused';
            this.healthySince = undefined;
        }
    }

    resumedExternally(): boolean {
        if (this.current !== 'paused' && this.current !== 'promptResume') {
            return false;
        }
        this.toIdle();
        return true;
    }

    dismiss(now: number): boolean {
        if (this.current === 'promptPause') {
            this.quietUntil = now + COOLDOWN_MS;
            this.toIdle();
            return true;
        }
        if (this.current === 'promptResume') {
            this.quietUntil = now + COOLDOWN_MS;
            this.current = 'paused';
            this.healthySince = undefined;
            return true;
        }
        return false;
    }

    silence(now: number): void {
        this.quietUntil = now + COOLDOWN_MS;
    }

    reset(): void {
        this.toIdle();
        this.quietUntil = 0;
    }

    private toIdle(): void {
        this.current = 'idle';
        this.degradedSince = undefined;
        this.healthySince = undefined;
    }

    private watchForDegradation(now: number, sample: ReliefSample): void {
        if (!sample.videoActive || !sample.degraded || now < this.quietUntil) {
            this.degradedSince = undefined;
            return;
        }
        this.degradedSince ??= now;
        this.currentReason = sample.degraded;
        if (now - this.degradedSince >= DEGRADED_MS) {
            this.current = 'promptPause';
        }
    }

    private watchForRecovery(now: number, sample: ReliefSample): void {
        if (!sample.healthy || now < this.quietUntil) {
            this.healthySince = undefined;
            return;
        }
        this.healthySince ??= now;
        if (now - this.healthySince >= HEALTHY_MS) {
            this.current = 'promptResume';
        }
    }
}
